import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { ProductDetails } from '@/components/blocks/FreeGiftV2/parts/ProductDetails';
import { DrawerContentSx, DrawerRewardSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import { RewardTarget, SkuQuantity } from '@/components/blocks/FreeGiftV2/types';
import {
	MarketInventoryIndex,
	isEveryGiftOutOfStock,
	isFixedRewardGroupOutOfStock,
	isOutOfStock,
} from '@/components/blocks/FreeGiftV2/utils';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Alert, Box, Checkbox, Chip, Radio, Typography, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

export const DrawerReward = () => {
	const labels = useLocalization('FreeGift');
	const theme = useTheme();
	const {
		currentPartNumber,
		purchase,
		purchaseQty,
		othersQty,
		reward,
		rewardsQty: baseRewardsQty,
		marketInventory,
	} = useFreeGiftPromotion();
	const {
		openDrawer,
		promotionQty,
		selectedPurchaseGroupId,
		selectedPurchaseSkus,
		selectedRewardGroupId,
		setSelectedRewardGroupId,
		selectedRewardSkus,
		setSelectedRewardSkus,
	} = useFreeGiftInteraction();
	const isCombinable = reward?.combinable;
	const groups = reward?.rewards ?? [];
	const rewardsQty = baseRewardsQty * promotionQty;

	const currentProductFirst = (skus: SkuQuantity[]) =>
		[...skus].sort(
			(a, b) => Number(b.sku === currentPartNumber) - Number(a.sku === currentPartNumber)
		);

	const isPurchaseComplete = purchase?.combinable
		? selectedPurchaseSkus.reduce((sum, item) => sum + item.quantity, 0) >=
		  (purchaseQty + othersQty) * promotionQty
		: Boolean(selectedPurchaseGroupId);

	const rewardProducts = reward?.rewards?.[0]?.targetSKUs ?? [];
	const parentReward = rewardProducts.find((sku) => sku.sku === currentPartNumber);
	const hasSingleGiftOption = rewardProducts.length === 1;
	const hasSingleGiftOptionFixed = reward?.rewards?.length === 1;

	const isGroupOutOfStock = (
		group: RewardTarget,
		inventory: MarketInventoryIndex = marketInventory
	) => isFixedRewardGroupOutOfStock(group, promotionQty, inventory);

	const previousPromotionQtyRef = useRef(promotionQty);

	useEffect(() => {
		if (isCombinable && promotionQty < previousPromotionQtyRef.current) {
			setSelectedRewardSkus(parentReward ? [{ sku: parentReward.sku, quantity: 1 }] : []);
		}

		previousPromotionQtyRef.current = promotionQty;
	}, [isCombinable, promotionQty, parentReward, setSelectedRewardSkus]);

	useEffect(() => {
		if (isCombinable) {
			return;
		}

		const selectedGroup = groups.find((group) => group.targetGroupId === selectedRewardGroupId);

		if (!selectedGroup) {
			return;
		}

		setSelectedRewardSkus(
			selectedGroup.targetSKUs.map((sku) => ({ ...sku, quantity: sku.quantity * promotionQty }))
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCombinable, groups, selectedRewardGroupId, promotionQty]);

	const hasAppliedDefaultGroupRef = useRef(false);

	useEffect(() => {
		if (isCombinable || hasAppliedDefaultGroupRef.current) {
			return;
		}

		hasAppliedDefaultGroupRef.current = true;

		if (selectedRewardGroupId) {
			return;
		}

		const availableGroups = groups.filter((group) => !isGroupOutOfStock(group));

		if (availableGroups.length !== 1) {
			return;
		}

		const defaultGroup = availableGroups[0];

		setSelectedRewardGroupId(defaultGroup.targetGroupId);
		setSelectedRewardSkus(
			defaultGroup.targetSKUs.map((sku) => ({ ...sku, quantity: sku.quantity * promotionQty }))
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCombinable, groups, marketInventory]);

	const isParentOnlyGiftOption = hasSingleGiftOption && Boolean(parentReward);

	useEffect(() => {
		if (!isCombinable || !parentReward) {
			return;
		}

		if (isOutOfStock(marketInventory[parentReward.sku])) {
			return;
		}

		if (isParentOnlyGiftOption) {
			setSelectedRewardSkus([{ sku: parentReward.sku, quantity: rewardsQty }]);
			return;
		}

		setSelectedRewardSkus((current) => {
			const hasParentSelected = current.some((item) => item.sku === parentReward.sku);
			return hasParentSelected ? current : [...current, { sku: parentReward.sku, quantity: 1 }];
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCombinable, parentReward, isParentOnlyGiftOption, rewardsQty, marketInventory]);

	useEffect(() => {
		if (!isCombinable || !hasSingleGiftOption || parentReward) {
			return;
		}

		const firstCandidate = rewardProducts[0];

		if (!firstCandidate || isOutOfStock(marketInventory[firstCandidate.sku])) {
			return;
		}

		setSelectedRewardSkus([{ sku: firstCandidate.sku, quantity: rewardsQty }]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCombinable, hasSingleGiftOption, parentReward, rewardsQty, marketInventory]);

	const wasDrawerOpenRef = useRef(openDrawer);

	useEffect(() => {
		const wasOpen = wasDrawerOpenRef.current;
		wasDrawerOpenRef.current = openDrawer;

		if (wasOpen || !openDrawer) {
			return;
		}

		if (isCombinable) {
			setSelectedRewardSkus((current) => current.filter((item) => marketInventory[item.sku] !== 0));
			return;
		}

		const selectedGroup = groups.find((group) => group.targetGroupId === selectedRewardGroupId);

		if (selectedGroup && isGroupOutOfStock(selectedGroup)) {
			setSelectedRewardGroupId(undefined);
			setSelectedRewardSkus([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openDrawer]);

	const selectSingleGift = (sku: string) => {
		setSelectedRewardSkus([{ sku, quantity: rewardsQty }]);
	};

	const disabledOverlay = !isPurchaseComplete ? <Box sx={DrawerRewardSx.disableOverlay} /> : null;

	const noGiftSelectable = isEveryGiftOutOfStock(reward, promotionQty, marketInventory);

	const noGiftSelectableAlert = noGiftSelectable ? (
		<Alert
			severity="error"
			icon={
				<DynamicIcon
					name={MATERIAL_SYMBOL_ICONS.cancel}
					color={theme.palette.error.dark}
					size="24px"
				/>
			}
			sx={DrawerContentSx.insufficientItemsAlert}
		>
			{labels.InsufficientItemsForPromotion.t()}
		</Alert>
	) : null;

	if (!isCombinable) {
		return (
			<>
				{!hasSingleGiftOptionFixed && (
					<Typography variant="bodyBaseline" color="grey.500">
						{labels.ChoseNGifts.t({ n: 1 })}
					</Typography>
				)}
				{noGiftSelectableAlert}
				<Box sx={isPurchaseComplete ? DrawerRewardSx.containerComplete : DrawerRewardSx.container}>
					{reward?.rewards?.map((group) => {
						const isSelected = group.targetGroupId === selectedRewardGroupId;
						const isSingleSelfItem =
							groups.length === 1 &&
							group.targetSKUs.length === 1 &&
							group.targetSKUs[0].sku === currentPartNumber;
						const groupOutOfStock = isGroupOutOfStock(group);

						return (
							<Box
								key={group.targetGroupId}
								sx={isSelected ? DrawerRewardSx.groupSelected : DrawerRewardSx.group}
							>
								{groupOutOfStock ? <Box sx={DrawerRewardSx.disableOverlay} /> : null}
								{isSingleSelfItem ? (
									<Chip
										label={labels.ParentProduct.t()}
										size="small"
										sx={DrawerRewardSx.parentProductChip}
									/>
								) : null}
								<Box display="flex">
									<Radio
										checked={isSelected}
										onChange={() => setSelectedRewardGroupId(group.targetGroupId)}
										disabled={!isPurchaseComplete || groupOutOfStock}
										disableFocusRipple
										disableRipple
										sx={DrawerRewardSx.fields}
									/>
									<Box display="flex" flexDirection="column" gap="15px">
										{currentProductFirst(group.targetSKUs).map((sku: SkuQuantity) => (
											<ProductDetails
												key={sku.sku}
												sku={sku.sku}
												isParentProduct={isSingleSelfItem ? false : sku.sku === currentPartNumber}
												quantity={sku.quantity * promotionQty}
												selected={isSelected}
												outOfStock={sku.sku !== currentPartNumber && groupOutOfStock}
											/>
										))}
									</Box>
								</Box>
							</Box>
						);
					})}
					{disabledOverlay}
				</Box>
			</>
		);
	}

	const totalSelectedQty = selectedRewardSkus.reduce((sum, item) => sum + item.quantity, 0);

	const updateSkuQuantity = (sku: string, quantity: number) => {
		const others = selectedRewardSkus.filter((item) => item.sku !== sku);
		const othersTotal = others.reduce((sum, item) => sum + item.quantity, 0);
		const availableQty = marketInventory[sku];
		const clampedQuantity = Math.max(
			0,
			Math.min(quantity, rewardsQty - othersTotal, availableQty ?? Infinity)
		);

		setSelectedRewardSkus(
			clampedQuantity > 0 ? [...others, { sku, quantity: clampedQuantity }] : others
		);
	};

	const handleToggle = (sku: string, isParentProduct: boolean, checked: boolean) => {
		if (!checked) {
			updateSkuQuantity(sku, 0);
			return;
		}

		updateSkuQuantity(sku, isParentProduct ? 1 : Math.min(1, rewardsQty - totalSelectedQty));
	};

	return (
		<>
			<Typography variant="bodyBaseline" color="grey.500">
				{labels.ChoseNGifts.t({ n: rewardsQty })}
			</Typography>
			{noGiftSelectableAlert}
			<Box sx={isPurchaseComplete ? DrawerRewardSx.containerComplete : DrawerRewardSx.container}>
				{currentProductFirst(rewardProducts).map((sku: SkuQuantity) => {
					const isParentProduct = sku.sku === currentPartNumber;
					const currentQty = selectedRewardSkus.find((item) => item.sku === sku.sku)?.quantity ?? 0;
					const isSelected = isParentProduct || currentQty > 0;
					const remainingForThisItem = rewardsQty - totalSelectedQty + currentQty;
					const availableQty = marketInventory[sku.sku];
					const outOfStock = isOutOfStock(availableQty);
					const isQuotaLocked =
						isPurchaseComplete &&
						!hasSingleGiftOption &&
						!isParentProduct &&
						!isSelected &&
						totalSelectedQty >= rewardsQty;

					return (
						<Box
							display="flex"
							flexDirection="column"
							key={sku.sku}
							sx={
								isSelected ? DrawerRewardSx.combinableGroupSelected : DrawerRewardSx.combinableGroup
							}
						>
							{isQuotaLocked ? <Box sx={DrawerRewardSx.disableOverlay} /> : null}
							{isParentProduct ? (
								<Chip
									label={labels.ParentProduct.t()}
									size="small"
									sx={DrawerRewardSx.parentProductChip}
								/>
							) : null}
							<Box display="flex">
								{hasSingleGiftOption ? (
									<Radio
										checked={isSelected}
										disabled={!isPurchaseComplete || isParentProduct || outOfStock}
										onChange={() => selectSingleGift(sku.sku)}
										disableFocusRipple
										disableRipple
										sx={DrawerRewardSx.fields}
									/>
								) : (
									<Checkbox
										checked={isSelected}
										disabled={
											!isPurchaseComplete ||
											isParentProduct ||
											outOfStock ||
											(!isSelected && totalSelectedQty >= rewardsQty)
										}
										onChange={(e) => handleToggle(sku.sku, isParentProduct, e.target.checked)}
										disableFocusRipple
										disableRipple
										disableTouchRipple
										sx={DrawerRewardSx.checkboxFields}
									/>
								)}
								<ProductDetails
									sku={sku.sku}
									isParentProduct={hasSingleGiftOption ? false : isParentProduct}
									quantity={currentQty}
									combinable={!hasSingleGiftOption}
									selected={isSelected}
									maxQuantity={Math.min(remainingForThisItem, availableQty ?? Infinity)}
									onQuantityChange={(qty) => updateSkuQuantity(sku.sku, qty)}
									outOfStock={outOfStock}
								/>
							</Box>
						</Box>
					);
				})}
				{disabledOverlay}
			</Box>
		</>
	);
};
